import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { LimitedLengthStack } from '@frontend/util/limited-length-stack';
import { EntitlementsService } from 'entitlements';
import { EnvService } from 'frontend-environment';
import { ButtonModule } from 'primeng/button';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Subscription,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  withLatestFrom,
} from 'rxjs/operators';

/* If we look for an env key,
   that key is provided to this component
   via the route data. That's why this component
   specifies it's env as Record<string,string>.

   This is so that there can be multiple
   individually configured Brownfield fallback
   portions of the app, pointed to different
   brownfield apps.
 */
export type BrownfieldConnectorEnv = Record<string, string>;

@Component({
  selector: 'ts-brownfield-fallback',
  imports: [CommonModule, ButtonModule],
  templateUrl: './brownfield-fallback.component.html',
  styleUrls: ['./brownfield-fallback.component.scss'],
})
export class BrownfieldFallbackComponent implements OnInit, OnDestroy {
  private domSanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private window = inject(Window);
  private env = inject<EnvService<BrownfieldConnectorEnv>>(EnvService);
  private entitlementService = inject(EntitlementsService);

  notLimitedView$ = this.entitlementService
    .getEntitlement$({
      requireNotLimitedView: true,
    })
    .pipe(map((v) => (v === 'control' ? false : v)));

  /**
   * The root domain & path to host in the iframe.
   * Example: https://brownfield.com
   */
  brownfieldRootUrl$ = this.route.data.pipe(
    map((data): string => {
      if ('brownfieldRootUrlEnvKey' in data) {
        const envKey = data['brownfieldRootUrlEnvKey'] as string;
        if (!envKey)
          throw Error('Failed to get brownfieldRootUrlEnvKey from route data');
        const brownfieldRootUrl = this.env[envKey];
        if (!brownfieldRootUrl)
          throw Error(
            `Failed to get brownfieldRootUrl from env using env key [${envKey}]`,
          );
        return brownfieldRootUrl;
      }
      return data['brownfieldRootUrl'] || '';
    }),
    map((rootUrl) => new URL(rootUrl).origin),
  );

  /**
   * (optional)
   * root path in Greenfield project where the Brownfield
   * app is hosted. default: ''
   *
   * Example: if Brownfield is hosted under https://green.com/old-stuff/....
   * then brownfieldPathPrefix would be 'old-stuff/'
   */
  brownfieldPathPrefix$ = this.route.data.pipe(
    map((data): string => data['brownfieldPathPrefix'] || ''),
    map((path) => {
      const url = new URL('http://fake.com');
      url.pathname = path;
      let cleanPath = url.pathname;
      if (cleanPath.endsWith('/')) {
        cleanPath = cleanPath.slice(0, cleanPath.length - 1);
      }
      return cleanPath;
    }),
    shareReplay(),
  );

  @ViewChild('iframe', { read: ElementRef })
  iframeRef: ElementRef;

  routerSub: Subscription;

  // for manually setting a path in the iframe
  brownfieldPath$ = new BehaviorSubject('');
  // are we on the adex homepage?
  isAdexHome$ = new BehaviorSubject(false);

  /**
   * Keeps track of URLs Greenfield has navigated
   * the iframe to. These navigations result in double-entry
   * in the browser history, so when the iframe goes "back" to
   * one of these URLs, we need to trigger another "back" to
   * keep the experiences somewhat sane.
   *
   * currentUrl: keeps track of the iframe's current URL
   * through navigation, including push/pop/replaceState.
   */
  outerNavigationHistory = new LimitedLengthStack<string>(50);
  currentUrl = '';

  iframeSrc$ = combineLatest(
    [this.brownfieldRootUrl$, this.brownfieldPathPrefix$, this.brownfieldPath$],
    (rootUrl, pathPrefix, customPath) => {
      let path = this.router.url;
      if (path.startsWith(pathPrefix)) {
        path = path.replace(pathPrefix, '');
      }
      // ensure that there will be 1 and only 1 '/' when joining
      // the root with the path
      if (path.startsWith('/')) {
        path = path.slice(1);
      }
      if (!rootUrl.endsWith('/')) {
        rootUrl = `${rootUrl}/`;
      }
      return this.domSanitizer.bypassSecurityTrustResourceUrl(
        customPath ? `${rootUrl}${customPath}` : `${rootUrl}${path}`,
      );
    },
  ).pipe(distinctUntilChanged());

  handleMessage = async ({ data }: MessageEvent) => {
    // only respond to messages tagged for greenfieldBridge and navigation
    if (!(data.greenfieldBridge && data.navigation)) return;

    if (await this.handleUrlForMonoApp(data)) {
      return;
    }

    switch (data.type) {
      case 'external':
        this.window.location = data.location;
        break;
      case 'internal':
      case 'replaceState':
      case 'pushState': {
        if (data.title) this.window.document.title = data.title;

        this.currentUrl = data.location;
        const brownfieldPathPrefix = await firstValueFrom(
          this.brownfieldPathPrefix$,
        );
        const currentPath = this.window.location.href.replace(
          this.window.location.origin + brownfieldPathPrefix,
          '',
        );
        if (currentPath !== data.location) {
          let newPath = data.location || '';

          // If absolute URL, strip down to pathname+search+hash
          try {
            const url = new URL(newPath, this.window.location.origin);
            newPath = url.pathname + url.search + url.hash;
          } catch {
            // not a valid absolute URL, leave it as-is
          }

          //  Prepend brownfield prefix
          newPath = `${brownfieldPathPrefix}${newPath}`;

          // Collapse duplicate slashes
          newPath = newPath.replace(/\/{2,}/g, '/');

          // Ensure prefix only once
          const searchRegex = new RegExp(`^(\/?${brownfieldPathPrefix})+`);
          newPath = newPath.replace(searchRegex, `/${brownfieldPathPrefix}/`);

          //  Safe replaceState
          try {
            this.window.history.replaceState({}, '', newPath);
          } catch (e) {
            console.warn(
              'Skipping invalid history.replaceState for',
              newPath,
              e,
            );
          }
        }
        break;
      }

      case 'popState': {
        if (data.title) this.window.document.title = data.title;

        this.currentUrl = data.location;
        if (data.location === this.outerNavigationHistory.peek()) {
          this.outerNavigationHistory.pop();
          this.window.history.back();
        }
        break;
      }
    }
  };

  async handleUrlForMonoApp(data: any): Promise<boolean> {
    this.isAdexHome$.next(data['location'] === '/home');
    const url = this.parseUrlForMonoApp(data['location']);
    if (url === null) return false;

    // Alright we are in here and this looks like it is a url for a sub-app and I want to get it handled
    this.router.navigateByUrl(url);
    return true;
  }

  parseUrlForMonoApp(url: string): string | null {
    if (this.env['REWRITE_BROWNFIELD_URLS_FOR_MONO_APP'] !== 'true') {
      return null;
    }

    let prefix = '';
    if (url.startsWith('https://adex.demandscienceplatform')) {
      prefix = 'adex';
    } else if (url.startsWith('https://measurement.demandscienceplatform')) {
      prefix = 'measure-studio';
    } else if (url.startsWith('https://data.demandscienceplatform')) {
      prefix = 'data-studio';
    }

    if (prefix === '') return null;

    return url.replace(
      /https:\/\/[^.]+.demandscienceplatform.[^/]+\//,
      `/${prefix}/`,
    );
  }

  handleOuterNavigationUpdate = (
    location: string,
    brownfieldRootUrl: string,
    pathPrefix: string,
  ) => {
    this.outerNavigationHistory.push(this.currentUrl);

    location = location.replace(pathPrefix, '');

    this.iframeRef.nativeElement.contentWindow.postMessage(
      { greenfieldBridge: true, navigation: true, location },
      '*',
    );
  };

  navigateToCreateCampaign() {
    this.router.navigate(['/adex/create-a-campaign']);
  }

  navigateToCreativeReport() {
    this.isAdexHome$.next(false);
    this.brownfieldPath$.next('/aggregate-creatives');
  }

  navigateToCampaignReport() {
    this.isAdexHome$.next(false);
    this.router.navigate(['adex/aggregate-campaigns']);
  }

  ngOnInit() {
    window.addEventListener('message', this.handleMessage);
    this.routerSub = this.router.events
      .pipe(
        filter(
          (ev): ev is NavigationEnd | NavigationSkipped =>
            /* allow NavigationSkipped events in the event we are routing from a deep BF page back to its GF root path
             * GF router thinks it's still on the same GF page (isn't aware of navigations within the BF iframe) so it skips the navigation
             * this change allows the navigation back to the root GF path */
            ev instanceof NavigationEnd || ev instanceof NavigationSkipped,
        ),
        map((ev) =>
          ev instanceof NavigationEnd ? ev.urlAfterRedirects : ev.url,
        ),
        withLatestFrom(this.brownfieldRootUrl$, this.brownfieldPathPrefix$),
      )
      .subscribe(([location, brownfieldRootUrl, pathPrefix]) => {
        this.handleOuterNavigationUpdate(
          location,
          brownfieldRootUrl,
          pathPrefix,
        );
      });
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.handleMessage);
    this.routerSub?.unsubscribe();
  }
}
